import { useState, useEffect, Fragment } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Box, 
  Button, 
  Typography, 
  Badge,
  Card,
  CardContent,
  Collapse,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import ArchiveIcon from '@mui/icons-material/Archive';
import BuildIcon from '@mui/icons-material/Build';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import HistoryIcon from '@mui/icons-material/History';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsIcon from '@mui/icons-material/Directions';
import AddIcon from '@mui/icons-material/Add';

// Custom Components
import {
  Spinner,
  PropertyDetailsTable,
  PropertyClientsTable,
  PropertyMediaTable,
  BrowserTitle,
  PropertyProposalsTable,
  PropertyAppointmentsTable,
  PropertyEquipmentTable,
  PropertyArchiveTable,
  PropertyLegacyProposalsTable,
} from '../components';

// Utils
import messages from '../static/messages';
import { stringifyAddress } from '../utils/formatter';

// Services
import propertiesServices from '../services/propertiesServices';

// Hooks
import useAccordion from '../hooks/useAccordion';
import PropertyWarrantyTickets from '../components/PropertyWarrantyTickets';

// External variables
const blocks = ['details', 'clients', 'appointments', 'archive', 'proposals', 'legacyProposals', 'equipment', 'media', 'warrantyTickets'];

// Mobile Section Card Component
const MobileSectionCard = ({ title, icon: Icon, count, children, defaultExpanded = false, color = '#4caf50', action }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  return (
    <Card variant="outlined" sx={{ mb: 2, borderColor: expanded ? color : '#e0e0e0' }}>
      <Box 
        onClick={() => setExpanded(!expanded)}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          cursor: 'pointer',
          bgcolor: expanded ? `${color}10` : 'transparent',
          '&:hover': { bgcolor: `${color}10` },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Icon sx={{ color, fontSize: 24 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {count !== undefined && count > 0 && (
            <Chip 
              label={count} 
              size="small" 
              sx={{ 
                bgcolor: color, 
                color: '#fff', 
                fontWeight: 600,
                height: 22,
                minWidth: 28,
              }} 
            />
          )}
          {action && (
            <Box sx={{ ml: 1 }}>{action}</Box>
          )}
        </Box>
        <ExpandMoreIcon 
          sx={{ 
            color: '#666',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }} 
        />
      </Box>
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0 }}>
          {children}
        </CardContent>
      </Collapse>
    </Card>
  );
};

// `propertyId` + `embedded` let other pages open this view inside a modal
// (e.g. Low-Price Requests → click on the address): the id comes from the
// prop instead of the route, and navigation chrome (Back button, browser
// title) is hidden.
export const Property = ({ propertyId = null, embedded = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [property, setProperty] = useState();
  const [loadFailed, setLoadFailed] = useState(false);
  const [warrantyTicketsCount, setWarrantyTicketsCount] = useState(0);
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = userData?.role || '';

  const { id: routeId } = useParams();
  const id = propertyId || routeId;
  const { open, accordionToggle } = useAccordion(blocks);
  const navigate = useNavigate();
  const location = useLocation();

  // Async functions
  const getPropertyData = async () => {
    try {
      const response = await propertiesServices.getPropertyById(id);

      if (response.status === 200) {
        setProperty(response.data);
      } else {
        setLoadFailed(true);
        toast.error(messages.errors.error_data_loaning);
      }
    } catch (error) {
      if (error.response?.data?.status === 404) {
        if (embedded) {
          // inside a modal a redirect would hijack the host page
          setLoadFailed(true);
          return;
        }
        navigate('/404');
        return;
      }
      // Drop the spinner instead of hanging forever on timeout / 5xx / network errors
      setLoadFailed(true);
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  // Hooks
  useEffect(() => {
    getPropertyData();
    accordionToggle('details');
  }, []);

  if (!property) {
    if (loadFailed) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load property
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            The request timed out or the server returned an error. Please try again.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setLoadFailed(false);
              getPropertyData();
            }}
          >
            Retry
          </Button>
        </Box>
      );
    }
    return <Spinner />;
  }

  // Calculate counts
  const clientsCount = property?.clients?.length || 0;
  const appointmentsCount = property?.appointments?.length || 0;
  const archiveCount = property?.archiveAppointments?.length || 0;
  const proposalsCount = property?.proposals?.length || 0;
  const legacyProposalsCount = property?.legacyProposals?.length || 0;
  const equipmentCount = property?.equipment?.length || 0;
  const mediaCount = property?.media?.length || 0;

  // Build address
  const fullAddress = stringifyAddress(property, true);
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;

  return (
    <Box component="section">
      {!embedded && <BrowserTitle title={`Property: ${property?.address} | Airmaxx Pro`} />}

      {/* Back Button (hidden when shown inside a modal) */}
      {!embedded && (
        <Button
          variant="text"
          onClick={() =>
            navigate(
              `/properties${
                location.state ? `?page=${location?.state?.page}&per_page=${location?.state?.rowsPerPage}` : ''
              }`,
            )
          }
          startIcon={<KeyboardBackspaceIcon />}
          sx={{ mb: 2 }}
        >
          Back to Properties
        </Button>
      )}

      {/* Mobile Header Card */}
      {isMobile && (
        <Card 
          variant="outlined" 
          sx={{ 
            mb: 3, 
            borderColor: '#4caf50',
            borderWidth: 2,
            background: 'linear-gradient(135deg, #e8f5e9 0%, #fff 100%)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Box sx={{ 
                bgcolor: '#4caf50', 
                borderRadius: '50%', 
                width: 50, 
                height: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <HomeIcon sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                {property?.type?.name && (
                  <Chip 
                    label={property.type.name} 
                    size="small" 
                    sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', mb: 1 }}
                  />
                )}
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                  {property?.streetNumber} {property?.streetName}
                  {property?.unit && ` #${property.unit}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {property?.city}, {property?.state} {property?.zipCode}
                </Typography>
              </Box>
            </Box>
            
            {/* Navigate Button */}
            <Button
              component="a"
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              fullWidth
              startIcon={<DirectionsIcon />}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
            >
              Navigate to Property
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Mobile View */}
      {isMobile ? (
        <Box>
          <MobileSectionCard 
            title="Property Details" 
            icon={HomeIcon} 
            defaultExpanded 
            color="#4caf50"
          >
            <PropertyDetailsTable {...{ property, id, refresh: getPropertyData }} />
          </MobileSectionCard>

          <MobileSectionCard 
            title="Clients" 
            icon={PersonIcon} 
            count={clientsCount}
            defaultExpanded
            color="#1976d2"
          >
            <PropertyClientsTable clientsList={property?.clients} id={id} refresh={getPropertyData} />
          </MobileSectionCard>

          <MobileSectionCard 
            title="Appointments" 
            icon={EventIcon} 
            count={appointmentsCount}
            defaultExpanded
            color="#ff9800"
            action={
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/appointments/create?propertyId=${id}`);
                }}
                sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' }, textTransform: 'none' }}
              >
                Create
              </Button>
            }
          >
            <PropertyAppointmentsTable appointments={property?.appointments} />
          </MobileSectionCard>

          {archiveCount > 0 && (
            <MobileSectionCard 
              title="Archive" 
              icon={ArchiveIcon} 
              count={archiveCount}
              color="#9e9e9e"
            >
              <PropertyArchiveTable appointments={property?.archiveAppointments} />
            </MobileSectionCard>
          )}

          <MobileSectionCard 
            title="Proposals" 
            icon={DescriptionIcon} 
            count={proposalsCount}
            defaultExpanded
            color="#9c27b0"
          >
            <PropertyProposalsTable proposals={property?.proposals} />
          </MobileSectionCard>

          {legacyProposalsCount > 0 && (
            <MobileSectionCard 
              title="Legacy Proposals" 
              icon={HistoryIcon} 
              count={legacyProposalsCount}
              color="#795548"
            >
              <PropertyLegacyProposalsTable 
                legacyProposals={property?.legacyProposals} 
                propertyId={id}
                refresh={getPropertyData}
              />
            </MobileSectionCard>
          )}

          <MobileSectionCard 
            title="Installed Equipment" 
            icon={BuildIcon} 
            count={equipmentCount}
            color="#607d8b"
          >
            <PropertyEquipmentTable
              equipment={property?.equipment}
              propertyId={id}
              proposals={property?.proposals}
              refresh={getPropertyData}
              userRole={userRole}
            />
          </MobileSectionCard>

          <MobileSectionCard 
            title="Warranty Tickets" 
            icon={BuildIcon} 
            count={warrantyTicketsCount}
            color="#f44336"
          >
            <PropertyWarrantyTickets propertyId={id} onCountChange={setWarrantyTicketsCount} />
          </MobileSectionCard>

          <MobileSectionCard 
            title="Media" 
            icon={PhotoLibraryIcon} 
            count={mediaCount}
            color="#e91e63"
          >
            <PropertyMediaTable mediaList={property?.media} id={id} refresh={getPropertyData} />
          </MobileSectionCard>
        </Box>
      ) : (
        /* Desktop View - Original Accordions */
        <Box>
          {/* Property Details */}
          <Accordion defaultExpanded onChange={() => accordionToggle('details')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Property Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PropertyDetailsTable {...{ property, id, refresh: getPropertyData }} />
            </AccordionDetails>
          </Accordion>

          {/* Clients */}
          <Accordion defaultExpanded onChange={() => accordionToggle('clients')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Clients ({clientsCount})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PropertyClientsTable clientsList={property?.clients} id={id} refresh={getPropertyData} />
            </AccordionDetails>
          </Accordion>

          {/* Appointments */}
          <Accordion defaultExpanded onChange={() => accordionToggle('appointments')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                <Typography variant="body1">Appointments ({appointmentsCount})</Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/appointments/create?propertyId=${id}`);
                  }}
                  sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' }, textTransform: 'none' }}
                >
                  Create Appointment
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <PropertyAppointmentsTable appointments={property?.appointments} />
            </AccordionDetails>
          </Accordion>

          {/* Archive */}
          {archiveCount > 0 && (
            <Accordion defaultExpanded onChange={() => accordionToggle('archive')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body1">Archive ({archiveCount})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <PropertyArchiveTable appointments={property?.archiveAppointments} />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Proposals */}
          <Accordion defaultExpanded onChange={() => accordionToggle('proposals')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Proposals ({proposalsCount})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PropertyProposalsTable proposals={property?.proposals} />
            </AccordionDetails>
          </Accordion>

          {/* Legacy Proposals */}
          {legacyProposalsCount > 0 && (
            <Accordion defaultExpanded={false} onChange={() => accordionToggle('legacyProposals')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">Legacy Proposals</Typography>
                  <Badge badgeContent={legacyProposalsCount} color="info" max={99} sx={{ ml: 2 }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <PropertyLegacyProposalsTable 
                  legacyProposals={property?.legacyProposals} 
                  propertyId={id}
                  refresh={getPropertyData}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Installed Equipment */}
          <Accordion defaultExpanded onChange={() => accordionToggle('equipment')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Installed Equipment ({equipmentCount})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PropertyEquipmentTable
                equipment={property?.equipment}
                propertyId={id}
                proposals={property?.proposals}
                refresh={getPropertyData}
                userRole={userRole}
              />
            </AccordionDetails>
          </Accordion>

          {/* Warranty Tickets */}
          <Accordion defaultExpanded={false} onChange={() => accordionToggle('warrantyTickets')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Warranty Tickets ({warrantyTicketsCount})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PropertyWarrantyTickets propertyId={id} onCountChange={setWarrantyTicketsCount} />
            </AccordionDetails>
          </Accordion>

          {/* Media */}
          <Accordion defaultExpanded onChange={() => accordionToggle('media')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Media ({mediaCount})</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PropertyMediaTable mediaList={property?.media} id={id} refresh={getPropertyData} />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );
};
